import type { BatMetrics, NormalizedProfile, TrimResult } from '@/types/profile';
import { parseModelName } from '@/types/library';
import { parseCFile } from '@/lib/parser/parseCFile';
import { parseTUR } from '@/lib/parser/parseTUR';
import { trimProfile } from '@/lib/parser/trimProfile';
import { normalizeProfile } from '@/lib/parser/normalizeProfile';
import { computeMetrics } from '@/lib/geometry/computeMetrics';

export interface FileGroup {
  /** File name without extension, e.g. "ML243-33" — used to derive model + size. */
  baseName: string;
  files: File[];
}

/** Groups a flat list of dropped files by their base name (before the extension) —
 * this is how "drop all 6 files for every model at once" gets sorted into one
 * group per model+size, regardless of how many models were dropped together. */
export function groupFilesByBaseName(files: File[]): FileGroup[] {
  const map = new Map<string, File[]>();
  for (const f of files) {
    const base = f.name.replace(/\.[^./\\]+$/, '');
    const list = map.get(base) ?? [];
    list.push(f);
    map.set(base, list);
  }
  return Array.from(map.entries()).map(([baseName, groupFiles]) => ({ baseName, files: groupFiles }));
}

export interface ProcessedGroup {
  sourceFormat: 'TUR' | 'C';
  metrics: BatMetrics;
  normalized: NormalizedProfile;
  trim: TrimResult;
}

export type ProcessGroupResult = { ok: true; data: ProcessedGroup } | { ok: false; error: string };

/** Runs the standard parse -> trim -> normalize -> metrics pipeline on one
 * file group, preferring .TUR (more complete real toolpath geometry) over
 * .C when both are present — the same preference used in the main Studio
 * importer. */
export async function processFileGroup(group: FileGroup): Promise<ProcessGroupResult> {
  const turFile = group.files.find((f) => /\.tur$/i.test(f.name));
  const cFile = group.files.find((f) => /\.c$/i.test(f.name));
  const primary = turFile ?? cFile;

  if (!primary) {
    return { ok: false, error: `"${group.baseName}" has no .C or .TUR file among its uploads — can't generate geometry.` };
  }

  const text = await primary.text();
  const isTUR = primary === turFile;
  const points = isTUR ? parseTUR(text) : parseCFile(text).points;

  if (points.length === 0) {
    return { ok: false, error: `"${primary.name}" has no valid rows.` };
  }

  const trim = trimProfile(points);
  if (trim.trimmedPoints.length < 2) {
    return { ok: false, error: `Could not isolate a physical profile from "${primary.name}".` };
  }

  const normalized = normalizeProfile(trim.trimmedPoints);
  const { modelName } = parseModelName(group.baseName);
  const metrics = computeMetrics(normalized, modelName, normalized.unit);

  return {
    ok: true,
    data: { sourceFormat: isTUR ? 'TUR' : 'C', metrics, normalized, trim },
  };
}
